import { useState } from 'react';
import HelpTooltip from '../atoms/HelpTooltip';
import BottomSheet from '../atoms/BottomSheet';
import { tooltipContent } from '../../content/tooltipContent';
import { PACING_OPTIONS, HUMOR_OPTIONS } from '../../content/personaOptions';
import styles from './Initializer.module.css';

const MAX_ACCENT_LENGTH = 80;

const formatLabel = (str) => {
  if (!str) return '';
  const words = str.replace(/[_-]/g, ' ').split(' ');
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

function StyleFormStep({
  personaData,
  setPersonaData,
  onNext,
  onBack,
  options,
  currentStepNumber,
  totalSteps,
}) {
  const [formData, setFormData] = useState({
    accent: personaData.communicationStyle?.accent || '',
    pacing: personaData.communicationStyle?.pacing || [],
    humorStyle: personaData.communicationStyle?.humorStyle || [],
    vocabularyComplexity:
      personaData.communicationStyle?.vocabularyComplexity || 'normal',
  });

  const [errors, setErrors] = useState({});
  const [activeTooltip, setActiveTooltip] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'accent' && value.length > MAX_ACCENT_LENGTH) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (field, value) => {
    const currentValues = formData[field] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];

    setFormData((prev) => ({ ...prev, [field]: newValues }));
  };

  const handleNext = () => {
    if (formData.accent.length > MAX_ACCENT_LENGTH) {
      setErrors({
        accent: `Accent description cannot exceed ${MAX_ACCENT_LENGTH} characters.`,
      });
      return;
    }
    setErrors({});
    setPersonaData((prevData) => ({
      ...prevData,
      communicationStyle: formData,
    }));
    onNext();
  };

  return (
    <div>
      <h3>
        Step {currentStepNumber}/{totalSteps}: Define Communication Style
      </h3>
      <div className={styles.formGroup}>
        <div className={styles.labelRow}>
          <label htmlFor="accent">
            Accent / Voice Description
            <HelpTooltip
              content={tooltipContent.accent}
              onMobileClick={setActiveTooltip}
            />
          </label>
          <span className={styles.charCounter}>
            {formData.accent.length} / {MAX_ACCENT_LENGTH}
          </span>
        </div>
        <input
          className={errors.accent ? styles.errorInput : ''}
          type="text"
          id="accent"
          name="accent"
          value={formData.accent}
          onChange={handleChange}
          placeholder="e.g., A calm, soothing British accent"
        />
        {errors.accent && <p className="errorText">{errors.accent}</p>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="vocabularyComplexity">Vocabulary Complexity</label>
        <select
          id="vocabularyComplexity"
          name="vocabularyComplexity"
          value={formData.vocabularyComplexity}
          onChange={handleChange}
        >
          {options?.vocabularyComplexities?.map((opt) => (
            <option key={opt} value={opt}>
              {formatLabel(opt)}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label>Pacing</label>
        <div className={styles.checkboxGrid}>
          {PACING_OPTIONS.map((opt) => (
            <label key={opt.value} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.checkboxInput}
                checked={formData.pacing.includes(opt.value)}
                onChange={() => handleCheckboxChange('pacing', opt.value)}
              />
              <span className={styles.checkboxControl}></span>
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label>Humor Style</label>
        <div className={styles.checkboxGrid}>
          {HUMOR_OPTIONS.map((opt) => (
            <label key={opt.value} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.checkboxInput}
                checked={formData.humorStyle.includes(opt.value)}
                onChange={() => handleCheckboxChange('humorStyle', opt.value)}
              />
              <span className={styles.checkboxControl}></span>
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div className={styles.buttonGroup}>
        <button onClick={onBack} className="action-button secondary">
          Back
        </button>
        <button onClick={handleNext} className="action-button primary">
          Next
        </button>
      </div>

      {/* Tooltip display area on mobile. */}
      <BottomSheet
        isOpen={!!activeTooltip}
        onClose={() => setActiveTooltip(null)}
      >
        {activeTooltip}
      </BottomSheet>
    </div>
  );
}

export default StyleFormStep;
